/**
 * App-koppeling: houdt de engine, schermen, audio, camera en opslag bij elkaar.
 * Alle wijzigingen gaan via emit() -> engine.handle -> persist -> render.
 */

import {
  buildAnnouncements,
  MatchEngine,
  playerNameAtOrderPosition,
  type DomainEvent,
  type Effect,
  type MatchConfig,
  type TeamId,
} from "../core";
import { SpeechAdapter } from "../audio/speech";
import { BrowserCameraAdapter, FrameLoop } from "../detect/camera";
import { ClipCapture, IndexedDbClipStore } from "../detect/clips";
import type { CameraAdapter, CameraFrameStats, FieldCalibration } from "../detect/types";
import { SimulatedDetectionAdapter } from "../detect/simulated";
import { MotionDetectionAdapter } from "../detect/motion";
import { VideoFileAdapter } from "../detect/videoFile";
import { clear } from "./dom";
import { renderCamera } from "./cameraView";
import { renderLive } from "./live";
import { renderResult } from "./result";
import { renderSetup } from "./setup";

export type ScreenName = "setup" | "live" | "camera" | "result";

export interface SetPlan {
  firstServeTeam: TeamId;
  firstInOrder: Record<TeamId, 0 | 1>;
  startSides: Record<TeamId, "left" | "right">;
}

export interface AppConfig {
  match: Partial<MatchConfig>;
  setPlans: [SetPlan, SetPlan, SetPlan];
  perfProfile: "low" | "mid" | "high";
}

const LS_EVENTS = "bsv2:events";
const LS_APP = "bsv2:app";
const LS_CAL = "bsv2:calibration";

export function defaultAppConfig(): AppConfig {
  return {
    match: {},
    setPlans: [
      { firstServeTeam: "A", firstInOrder: { A: 0, B: 0 }, startSides: { A: "left", B: "right" } },
      { firstServeTeam: "B", firstInOrder: { A: 0, B: 0 }, startSides: { A: "right", B: "left" } },
      { firstServeTeam: "A", firstInOrder: { A: 0, B: 0 }, startSides: { A: "left", B: "right" } },
    ],
    perfProfile: "mid",
  };
}

export function loadAppConfig(): AppConfig {
  try {
    const raw = localStorage.getItem(LS_APP);
    if (raw) return { ...defaultAppConfig(), ...JSON.parse(raw) };
  } catch {
    /* negeren */
  }
  return defaultAppConfig();
}

export function saveAppConfig(cfg: AppConfig): void {
  localStorage.setItem(LS_APP, JSON.stringify(cfg));
}

export class App {
  engine: MatchEngine;
  config: AppConfig = loadAppConfig();
  screen: ScreenName = "setup";
  root: HTMLElement;
  speech = new SpeechAdapter();
  sim = new SimulatedDetectionAdapter();
  cam = new BrowserCameraAdapter();
  fileSource = new VideoFileAdapter();
  motion = new MotionDetectionAdapter();
  frameLoop = new FrameLoop();
  clipStore = new IndexedDbClipStore();
  clipCapture = new ClipCapture();
  calibration: FieldCalibration | null = null;
  /** Fase 2: welke invoerbron is actief (camera of bestand). */
  activeSource: CameraAdapter | null = null;
  activeSourceKind: "camera" | "file" | null = null;
  activeVideo: HTMLVideoElement | null = null;
  activeStream: MediaStream | null = null;
  /** Vision-scoring aan/uit (motion-detector). */
  visionOn = false;
  /** Laatste Nederlandse status over camera/vision (fout of toestand). */
  visionStatus = "";
  private wakeLock: { release(): Promise<void> } | null = null;
  private lastToast: { text: string; kind: string; ts: number } | null = null;
  /** Laatste effecten (voor debugweergave). */
  lastEffects: Effect[] = [];

  constructor(root: HTMLElement) {
    this.root = root;
    this.engine = MatchEngine.fromEvents(this.loadEvents());
    this.loadCalibration();
    this.speech.loadVoices();
    if (this.engine.events.length > 0) {
      // Er is een bevroren log: ga terug naar het live scorebord (replay bij boot).
      this.screen = "live";
    }
    this.sim.start((ev) => this.emit(ev, "simulatie"));
    this.motion.setCameraLostHandler(() => this.handleCameraLost());
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  /* ------------------------- events ------------------------- */

  emit(ev: DomainEvent, source = "", persist = true): void {
    void source;
    const res = this.engine.handle(ev);
    this.lastEffects = res.effects;
    if (persist) {
      try {
        localStorage.setItem(LS_EVENTS, JSON.stringify(this.engine.events));
      } catch {
        /* vol of geblokkeerd: doorspelen zonder persist */
      }
    }
    this.handleEffects(res.effects);
    this.render();
  }

  private handleEffects(effects: Effect[]): void {
    const anns = buildAnnouncements(this.engine.state, effects);
    for (const a of anns) {
      this.toast(a.text, a.kind);
      if (a.kind !== "warning") this.speech.say(a.text);
    }
    // statuswijzigingen die gehint worden
    if (effects.some((e) => e.kind === "ask_user" || e.kind === "serve_deferred")) {
      this.toast("Controle nodig: één tik op Punt A of Punt B", "ask");
    }
  }

  private toast(text: string, kind: string): void {
    this.lastToast = { text, kind, ts: Date.now() };
    window.dispatchEvent(new CustomEvent("bsv2:toast", { detail: { text, kind } }));
  }

  get toastState(): { text: string; kind: string } | null {
    if (!this.lastToast) return null;
    return { text: this.lastToast.text, kind: this.lastToast.kind };
  }
  setToast(text: string, kind = ""): void {
    this.toast(text, kind);
  }

  /* ------------------------- navigatie ------------------------- */

  go(screen: ScreenName): void {
    this.screen = screen;
    if (screen === "live") void this.requestWakeLock();
    else void this.releaseWakeLock();
    this.render();
  }

  render(): void {
    // Bewaar de open-status van het debugpaneel vóór de re-render (het native
    // toggle-event van <details> is asynchroon en zou de status verliezen).
    const keepOpen = (this.root.querySelector("details.debug") as HTMLDetailsElement | null)?.open ?? false;
    clear(this.root);
    switch (this.screen) {
      case "setup":
        renderSetup(this, this.root);
        break;
      case "live":
        renderLive(this, this.root);
        break;
      case "camera":
        renderCamera(this, this.root);
        break;
      case "result":
        renderResult(this, this.root);
        break;
    }
    const dbg = this.root.querySelector("details.debug") as HTMLDetailsElement | null;
    if (dbg) dbg.open = keepOpen;
    if (this.lastToast) {
      const t = this.lastToast;
      const el = document.createElement("div");
      el.className = "toast";
      el.textContent = t.text;
      this.root.append(el);
      setTimeout(() => el.remove(), 4000);
    }
  }

  /* ------------------------- setstart ------------------------- */

  startMatch(cfg: MatchConfig, plan: SetPlan): void {
    this.engine = MatchEngine.fromEvents([]);
    this.engine.handle({ type: "MATCH_CONFIGURED", config: cfg, ts: Date.now() });
    this.lastEffects = [];
    this.startSet(1, plan);
    try {
      localStorage.setItem(LS_EVENTS, JSON.stringify(this.engine.events));
    } catch {
      /* negeren */
    }
  }

  startSet(setNumber: number, plan: SetPlan): void {
    if (this.engine.state.phase === "setup") {
      this.engine.handle({ type: "MATCH_CONFIGURED", config: this.resolveConfig(), ts: Date.now() });
    }
    this.emit(
      {
        type: "SET_STARTED",
        setNumber,
        firstServeTeam: plan.firstServeTeam,
        initialServers: plan.firstInOrder,
        startSides: plan.startSides,
        ts: Date.now(),
      },
      "set",
    );
  }

  resolveConfig(): MatchConfig {
    const cfg = this.config.match;
    return {
      bestOf: cfg.bestOf ?? 3,
      setTargets: cfg.setTargets ?? [21, 21, 15],
      minLead: cfg.minLead ?? 2,
      sideSwitchEvery: cfg.sideSwitchEvery ?? { normal: 7, thirdSet: 5 },
      highConfidence: cfg.highConfidence ?? 0.88,
      mediumConfidence: cfg.mediumConfidence ?? 0.65,
      authoritativeServeConfidence: cfg.authoritativeServeConfidence ?? 0.88,
      confirmServeConfidence: cfg.confirmServeConfidence ?? 0.65,
      teams: cfg.teams ?? {
        A: { name: "Team A", players: ["Speler A1", "Speler A2"] },
        B: { name: "Team B", players: ["Speler B1", "Speler B2"] },
      },
    };
  }

  playerLabel(team: TeamId, orderPos: 0 | 1): string {
    return playerNameAtOrderPosition(this.engine.state, team, orderPos);
  }

  /* ------------------------- persist ------------------------- */

  private loadEvents(): DomainEvent[] {
    try {
      const raw = localStorage.getItem(LS_EVENTS);
      if (raw) return JSON.parse(raw) as DomainEvent[];
    } catch {
      /* negeren */
    }
    return [];
  }

  private loadCalibration(): void {
    try {
      const raw = localStorage.getItem(LS_CAL);
      if (raw) this.calibration = JSON.parse(raw) as FieldCalibration;
    } catch {
      /* negeren */
    }
  }

  saveCalibration(cal: FieldCalibration | null): void {
    this.calibration = cal;
    if (cal) localStorage.setItem(LS_CAL, JSON.stringify(cal));
    else localStorage.removeItem(LS_CAL);
  }

  resetAll(): void {
    localStorage.removeItem(LS_EVENTS);
    this.engine = MatchEngine.fromEvents([]);
    this.screen = "setup";
    this.render();
  }

  /* ------------------------- wake lock ------------------------- */

  private async requestWakeLock(): Promise<void> {
    try {
      const nav = navigator as Navigator & { wakeLock?: { request(type: string): Promise<{ release(): Promise<void> }> } };
      if (!nav.wakeLock) return;
      this.wakeLock = await nav.wakeLock.request("screen");
      document.addEventListener("visibilitychange", this.onVis);
    } catch {
      /* niet beschikbaar (bv. iOS): nette fallback = niets */
    }
  }

  private async releaseWakeLock(): Promise<void> {
    document.removeEventListener("visibilitychange", this.onVis);
    try {
      await this.wakeLock?.release();
    } catch {
      /* negeren */
    }
    this.wakeLock = null;
  }

  private onVis = (): void => {
    if (document.visibilityState === "visible" && this.screen === "live") void this.requestWakeLock();
  };

  cameraStats(): CameraFrameStats {
    return this.frameLoop.stats;
  }

  /* ------------------------- fase 2: bron + vision ------------------------- */

  /**
   * Start de invoerbron (camera of bestand) voor preview/kalibratie/meting.
   * Fouten worden vertaald naar een Nederlandse status; de wedstrijd zelf
   * (engine/log) wordt NOOIT aangeraakt.
   */
  async startSource(kind: "camera" | "file", deviceId?: string): Promise<{ stream: MediaStream; video: HTMLVideoElement } | null> {
    this.stopVision();
    this.stopSource();
    const source = kind === "camera" ? this.cam : this.fileSource;
    try {
      const res = await source.start(deviceId);
      this.activeSource = source;
      this.activeSourceKind = kind;
      this.activeVideo = res.video;
      this.activeStream = res.stream;
      this.motion.attachVideo(res.video, res.stream);
      this.motion.setFieldCalibration(this.calibration);
      this.visionStatus = kind === "camera" ? "Camera actief — vision kan aan." : "Bestand actief — vision kan aan.";
      return res;
    } catch (err) {
      this.visionStatus = this.describeSourceError(err, kind);
      this.setToast(this.visionStatus, "warning");
      return null;
    }
  }

  stopSource(): void {
    this.stopVision();
    this.frameLoop.stop();
    this.clipCapture.dispose();
    this.activeSource?.stop();
    this.activeSource = null;
    this.activeSourceKind = null;
    this.activeVideo = null;
    this.activeStream = null;
  }

  startVision(): void {
    if (!this.activeVideo || !this.activeStream) {
      this.visionStatus = "Start eerst de camera of een bestand.";
      this.setToast(this.visionStatus, "warning");
      return;
    }
    this.motion.attachVideo(this.activeVideo, this.activeStream);
    this.motion.setFieldCalibration(this.calibration);
    this.motion.start((ev) => this.emit(ev, "vision"));
    this.visionOn = true;
    this.visionStatus = "Vision-scoring aan (motion-detector).";
  }

  stopVision(): void {
    if (this.visionOn || this.motion.videoElement) {
      this.motion.stop();
    }
    this.visionOn = false;
  }

  /** Camera verloren mid-wedstrijd: alleen vision stopt, de wedstrijd loopt door. */
  handleCameraLost(): void {
    this.stopVision();
    this.clipCapture.dispose();
    this.visionStatus = "Camera verloren tijdens de wedstrijd. Vision gestopt; Punt A/B blijft werken.";
    this.setToast(this.visionStatus, "warning");
    if (this.screen === "camera") this.render();
  }

  /** Tab naar achtergrond: vision stopt netjes; geen punt, log blijft geldig. */
  handleBackgrounded(): void {
    if (!this.visionOn) return;
    this.stopVision();
    this.visionStatus = "App ging naar de achtergrond — vision gestopt. Punt A/B blijft werken.";
    this.setToast(this.visionStatus, "warning");
  }

  private onVisibility = (): void => {
    if (document.visibilityState !== "hidden") return;
    this.handleBackgrounded();
  };

  private describeSourceError(err: unknown, kind: "camera" | "file"): string {
    const name = (err as DOMException | undefined)?.name ?? "";
    if (kind === "file") {
      return "Bestand kon niet worden afgespeeld (format of toestemming?).";
    }
    if (name === "NotAllowedError" || name === "SecurityError") {
      return "Cameratoestemming geweigerd. Vision werkt niet; Punt A/B blijft werken.";
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
      return "Geen camera gevonden. Vision werkt niet; Punt A/B blijft werken.";
    }
    if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
      return "Camera is in gebruik of defect. Vision werkt niet; Punt A/B blijft werken.";
    }
    return `Camera starten mislukt (${name || "onbekende fout"}). Punt A/B blijft werken.`;
  }
}
